import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { BackendErrorCode } from './backend-error-code';
import { BackendErrorContext, BackendErrorText } from './backend-error-text';
import { ClientErrorCode } from './client-error-code';

/**
 * Причина отказа бэкенда по коду — единственный словарь «код → текст» в проекте.
 *
 * Текст — **причина**, а не заголовок: его показывают после заголовка места вызова («Не удалось
 * удалить игрока. Игрок не найден — возможно, его уже удалили.»). Поэтому он годится для любого
 * действия: говорит, что случилось и что делать, без слов «не удалось …».
 *
 * Кода нет в словаре — тост показывает только заголовок места вызова. Так намеренно оставлены
 * без текста:
 * - коды, которые бэкенд не бросает (помечены в `BackendErrorCode`);
 * - коды с несколькими разными причинами (`STAGE_INVALID`, `MEDIA_KIND_NOT_SUPPORTED`): общий
 *   текст соврал бы; причины различают правила «по тексту» ниже (8005) или `overrides` места вызова;
 * - `IDEMPOTENCY` — это не отказ, а повтор запроса (IMGM-1561).
 *
 * Текст, зависящий от экрана, сюда не кладут — его передают `getErrorReason(error, { overrides })`.
 */
export const BACKEND_ERROR_MESSAGES: Partial<Record<BackendErrorCode, MessageDescriptor>> = {
  [BackendErrorCode.ONLY_CAPTAIN_ALLOWED]: msg`Это может сделать только капитан команды.`,
  [BackendErrorCode.INVALID_DATE]: msg`Дата указана неверно или период слишком длинный.`,
  [BackendErrorCode.NOT_ALLOWED]: msg`Недостаточно прав для этого действия.`,

  [BackendErrorCode.PHONE_NOT_FOUND]: msg`Пользователь с таким телефоном не найден.`,
  [BackendErrorCode.USER_NOT_FOUND]: msg`Игрок не найден — возможно, его уже удалили. Обновите страницу.`,

  [BackendErrorCode.INVITE_INVALID]: msg`Нельзя пригласить самого себя.`,

  [BackendErrorCode.TEAM_NOT_FOUND]: msg`Команда не найдена — возможно, её уже удалили. Обновите страницу.`,
  [BackendErrorCode.USER_NOT_IN_TEAM]: msg`Игрок не состоит в этой команде.`,
  [BackendErrorCode.TEAM_ALREADY_HAS_CAPTAIN]: msg`У команды уже есть капитан.`,
  [BackendErrorCode.CANNOT_DELETE_TEAM]: msg`У команды есть матчи — сначала удалите их.`,
  [BackendErrorCode.USER_ALREADY_IN_TEAM]: msg`Игрок уже состоит в этой команде.`,
  [BackendErrorCode.TEAM_NOT_IN_CHAMPIONSHIP]: msg`Команда не участвует в этом турнире.`,

  [BackendErrorCode.MATCH_NOT_FOUND]: msg`Матч не найден — возможно, его уже удалили. Обновите страницу.`,
  [BackendErrorCode.MATCH_INVALID]: msg`У матча не указаны команды.`,
  [BackendErrorCode.USER_NOT_IN_APPLICATION]: msg`Игрок не заявлен ни за одну из команд этого матча.`,
  [BackendErrorCode.MATCH_CANNOT_DELETE]: msg`Матч нельзя удалить: у него нет даты, он уже прошёл или начнётся меньше чем через 5 часов, или по нему уже есть события или посты.`,
  [BackendErrorCode.MATCH_NO_DATE]: msg`У матча не указана дата.`,

  [BackendErrorCode.ENTRY_REQUEST_INVALID]: msg`Заявка в команду заполнена неверно.`,
  [BackendErrorCode.ENTRY_REQUEST_USER_ALREADY_IN_TEAM]: msg`Игрок уже состоит в этой команде.`,

  [BackendErrorCode.TOURNAMENT_NOT_FOUND]: msg`Организатор не найден.`,

  [BackendErrorCode.CHAMPIONSHIP_NOT_FOUND]: msg`Турнир не найден — возможно, его уже удалили. Обновите страницу.`,
  [BackendErrorCode.USER_ALREADY_ACCEPTED_IN_ANOTHER_TEAM]: msg`Игрок уже заявлен за другую команду этого турнира.`,
  [BackendErrorCode.CHAMPIONSHIP_NOT_ACTIVE]: msg`Турнир не активен.`,

  [BackendErrorCode.STAGE_NOT_FOUND]: msg`Этап не найден — возможно, его уже удалили. Обновите страницу.`,

  [BackendErrorCode.ROUND_NOT_FOUND]: msg`Тур не найден — возможно, его уже удалили. Обновите страницу.`,
  [BackendErrorCode.ROUND_INVALID]: msg`Тур не относится к этому этапу или турниру.`,

  [BackendErrorCode.TEAM_GROUP_NOT_FOUND]: msg`Группа не найдена — возможно, её уже удалили. Обновите страницу.`,
  [BackendErrorCode.SYMBOLIC_TEAM_NOT_FOUND]: msg`Сборная не найдена — возможно, её уже удалили. Обновите страницу.`,
  [BackendErrorCode.TOURNAMENT_BRACKET_NOT_FOUND]: msg`Сетка не найдена — возможно, её уже пересоздали. Обновите страницу.`,
  [BackendErrorCode.NOTIFICATION_NOT_FOUND]: msg`Уведомление не найдено — возможно, его уже удалили. Обновите страницу.`,
  [BackendErrorCode.TOURNAMENT_PLAN_NOT_FOUND]: msg`Тариф не найден.`,
  [BackendErrorCode.ADDRESS_NOT_FOUND]: msg`Адрес не найден.`,

  [BackendErrorCode.LOCATION_NOT_FOUND]: msg`Локация не найдена — возможно, её уже удалили. Обновите страницу.`,
  // Отказ при **любом** матче в локации, и будущем тоже (`LocationMutation.php:92`), — не «сыгранные».
  [BackendErrorCode.LOCATION_IS_INVOLVED_IN_STATISTICS]: msg`К локации привязаны матчи — перенесите их в другую локацию или удалите.`,

  [BackendErrorCode.SOCIAL_MEDIA_NOT_FOUND]: msg`Соцсеть не найдена — возможно, её уже удалили. Обновите страницу.`,
  [BackendErrorCode.INCORRECT_URL_FOR_SOCIAL_MEDIA]: msg`Ссылка не подходит для этой соцсети.`,

  [BackendErrorCode.BILLING_NO_ACTIVE_SUBSCIPTION]: msg`У организатора нет активной подписки.`,
  [BackendErrorCode.BILLING_NO_ACTIVE_PLAN]: msg`У организатора нет активного тарифа.`,
  [BackendErrorCode.BILLING_NO_FEATURE]: msg`Эта возможность не входит в тариф организатора.`,
  [BackendErrorCode.BILLING_INVALID_AMOUNT]: msg`Сумма указана неверно.`,
  [BackendErrorCode.BILLING_PAYMENT_FAILED]: msg`Платёж не прошёл. Попробуйте позже или выберите другой способ оплаты.`,
  [BackendErrorCode.BILLING_TOPUP_NOT_FOUND]: msg`Пополнение не найдено.`,

  [BackendErrorCode.CHAMPIONSHIP_APPLICATION_NOT_FOUND]: msg`Заявка не найдена — возможно, её уже удалили. Обновите страницу.`,

  [BackendErrorCode.PREDICTION_MATCH_ALREADY_STARTED]: msg`Матч уже начался.`,
  [BackendErrorCode.PREDICTION_MATCH_HAS_RESULT]: msg`У матча уже есть результат.`,
  [BackendErrorCode.PREDICTION_ALREADY_EXISTS]: msg`Прогноз на этот матч уже сделан.`,
  [BackendErrorCode.PREDICTION_INVALID_TEAM]: msg`Команда не играет в этом матче.`,

  [BackendErrorCode.MEDIA_MANAGER_TEMPLATE_NOT_FOUND]: msg`Шаблон не найден — возможно, его уже удалили. Обновите страницу.`,
  [BackendErrorCode.MEDIA_MANAGER_TEMPLATE_NAME_CONFLICT]: msg`Такое название уже существует, придумайте новое`,

  [BackendErrorCode.MEDIA_MANAGER_TEMPLATE_GROUP_NOT_FOUND]: msg`Группа шаблонов не найдена — возможно, её уже удалили. Обновите страницу.`,
  [BackendErrorCode.MEDIA_MANAGER_TEMPLATE_GROUP_NAME_CONFLICT]: msg`Группа с таким названием уже есть.`,
  [BackendErrorCode.MEDIA_MANAGER_TEMPLATE_GROUP_NOT_EMPTY]: msg`В группе есть шаблоны — удалите или перенесите их.`,

  [BackendErrorCode.MEDIA_GROUP_NOT_OWNED]: msg`Набор шаблонов принадлежит другому организатору.`,
  [BackendErrorCode.MEDIA_JOB_NOT_FOUND]: msg`Картинка не найдена — возможно, её уже удалили. Обновите страницу.`,
  [BackendErrorCode.MEDIA_JOB_NOT_READY]: msg`Картинка ещё не готова или уже обработана.`,
  [BackendErrorCode.MEDIA_JOB_RENDERING]: msg`Картинка ещё рисуется — подождите.`,
  [BackendErrorCode.MEDIA_JOB_NOT_DELETABLE]: msg`Эту картинку уже нельзя удалить.`,
  [BackendErrorCode.MEDIA_TEMPLATE_KIND_MISSING]: msg`В наборе шаблонов нет шаблона для такой картинки.`,
  [BackendErrorCode.MEDIA_TIMEZONE_INVALID]: msg`Часовой пояс указан неверно.`,

  [BackendErrorCode.INTEGRATION_TYPE_INPUT_MISSING]: msg`Укажите канал Telegram.`,
  [BackendErrorCode.INTEGRATION_NOT_FOUND]: msg`Подключение не найдено — возможно, его уже удалили. Обновите страницу.`,
  [BackendErrorCode.INTEGRATION_ALREADY_EXISTS]: msg`К организации уже подключён канал. Удалите его, чтобы подключить другой.`,
  [BackendErrorCode.INTEGRATION_CONNECTION_FAILED]: msg`Бот не может публиковать в канале. Проверьте, что канал существует, бот добавлен в его администраторы с правом публикации сообщений, а адрес взят по инструкции выше: t.me/имя для публичного канала или адрес из web.telegram.org для закрытого.`,

  [BackendErrorCode.MOBILE_FEATURE_NOT_FOUND]: msg`Функция мобильного приложения не найдена.`,
};

/** Причина ошибки, которая возникла на стороне фронта. */
export const CLIENT_ERROR_MESSAGES: Record<ClientErrorCode, MessageDescriptor> = {
  [ClientErrorCode.NETWORK]: msg`Нет связи с сервером. Проверьте интернет и попробуйте ещё раз.`,
};

// ---------- по тексту ----------

/**
 * Правило раздела «по тексту»: как узнать случай `BackendErrorText` по сообщению бэкенда.
 *
 * Это **единственное** место в проекте, где ошибку сравнивают с английским текстом (стережёт
 * `src/shared/__tests__/backend-error-reasons.test.ts`, правило 4). Каждое правило — долг бэкенда:
 * у случая должен появиться свой код (`.claude/docs/backend-errors.md`).
 */
interface BackendErrorTextRule {
  text: BackendErrorText;
  /**
   * Код, при котором правило проверяется: один код — несколько причин. Не указан — правило
   * проверяется только у отказа **без** кода.
   */
  code?: BackendErrorCode;
  /** Только если место вызова назвало этот контекст: фраза слишком общая для всего проекта. */
  context?: BackendErrorContext;
  match: (message: string) => boolean;
}

const includes =
  (...needles: string[]) =>
  (message: string) =>
    needles.some((needle) => message.includes(needle));
const equals = (expected: string) => (message: string) => message === expected;
const matches = (pattern: RegExp) => (message: string) => pattern.test(message);

/** Первое совпадение выигрывает: частные фразы стоят раньше общих. */
export const BACKEND_ERROR_TEXT_RULES: readonly BackendErrorTextRule[] = [
  // 8005 — генерация матчей (`GenerateStageMatchesHandler.php:38,65,102,119`). Тот же код бросает
  // статистика («Stage does not belong to championship») — её фразы здесь не совпадут.
  { text: BackendErrorText.STAGE_TYPE_NOT_SUPPORTED, code: BackendErrorCode.STAGE_INVALID, match: includes('Unsupported stage type') },
  // «… Stage "<имя>" has no team groups.» тоже начинается с «Not enough teams» — поэтому раньше общего.
  { text: BackendErrorText.STAGE_HAS_NO_TEAM_GROUPS, code: BackendErrorCode.STAGE_INVALID, match: includes('has no team groups.') },
  // По началу фразы, а не по слову «Group»: имя этапа подставляется в текст и может его содержать.
  { text: BackendErrorText.GROUP_NOT_ENOUGH_TEAMS, code: BackendErrorCode.STAGE_INVALID, match: includes('matches. Group "') },
  { text: BackendErrorText.STAGE_NOT_ENOUGH_TEAMS, code: BackendErrorCode.STAGE_INVALID, match: includes('Not enough teams') },

  // 18025 — общий код провала пополнения (`BillingMutation.php:86-88`).
  {
    text: BackendErrorText.CARD_PAYMENTS_DISABLED,
    code: BackendErrorCode.BILLING_PAYMENT_FAILED,
    match: equals('Card payments are temporarily unavailable'),
  },

  // Без кода.
  { text: BackendErrorText.APPLICATION_LIMIT_EXCEEDED, match: includes('applications limit exceeded') },
  { text: BackendErrorText.NOT_ENOUGH_MONEY, context: BackendErrorContext.WALLET, match: equals('Not enough money') },
  // Только «email» в тексте. `Variable "$input" … got invalid value` — ошибка разбора **любого**
  // поля input (и суммы тоже), за «Укажите email» её больше не выдаём.
  {
    text: BackendErrorText.RECEIPT_EMAIL_INVALID,
    context: BackendErrorContext.WALLET,
    match: (message) => message.toLowerCase().includes('email'),
  },
  {
    text: BackendErrorText.VALUE_ALREADY_USED,
    context: BackendErrorContext.SIGN_UP_PHONE,
    match: equals('This value is already used.'),
  },

  // REST ссылки на заявку: всё, кроме `HttpException`, приходит 500 «Internal error: <текст>»
  // (`ExceptionListener.php:27-45`), поэтому по подстроке.
  {
    text: BackendErrorText.LINK_TOKEN_MISSING,
    context: BackendErrorContext.APPLICATION_LINK,
    match: includes('Missing required parameter "token"', 'Token not found'),
  },
  // lexik/jwt-authentication-bundle, `LcobucciJWTEncoder::decode`. «JWT Token not found» оттуда не
  // бросается (его бросает аутентификатор, а адреса ссылки — PUBLIC_ACCESS), правило удалено.
  { text: BackendErrorText.LINK_TOKEN_EXPIRED, context: BackendErrorContext.APPLICATION_LINK, match: includes('Expired JWT Token') },
  {
    text: BackendErrorText.LINK_TOKEN_INVALID,
    context: BackendErrorContext.APPLICATION_LINK,
    match: includes('Invalid JWT Token', 'Unable to verify', 'invalid token'),
  },
  { text: BackendErrorText.LINK_FIELD_NUMBER, context: BackendErrorContext.APPLICATION_LINK, match: includes('parameter "number"') },
  {
    text: BackendErrorText.LINK_FIELD_POSITION,
    context: BackendErrorContext.APPLICATION_LINK,
    match: includes('parameter "position"'),
  },
  { text: BackendErrorText.LINK_FIELD_NAME, context: BackendErrorContext.APPLICATION_LINK, match: includes('parameter "name"') },
  { text: BackendErrorText.LINK_FIELD_SURNAME, context: BackendErrorContext.APPLICATION_LINK, match: includes('parameter "surname"') },
  {
    text: BackendErrorText.LINK_FIELD_BIRTH_DATE,
    context: BackendErrorContext.APPLICATION_LINK,
    match: includes('parameter "birthDate"'),
  },
  {
    text: BackendErrorText.LINK_BIRTH_DATE_FORMAT,
    context: BackendErrorContext.APPLICATION_LINK,
    match: includes('Accepted format dd.mm.yyyy'),
  },
  {
    text: BackendErrorText.LINK_PHOTO_TYPE_NOT_ALLOWED,
    context: BackendErrorContext.APPLICATION_LINK,
    match: includes('File type not allowed'),
  },
  // «Could not get the content of the file» — Symfony `File::getContent()`, его зовёт
  // `ImageUploader.php:61`; «Could not upload file» — сам `ImageUploader.php:53`.
  {
    text: BackendErrorText.LINK_PHOTO_UPLOAD_FAILED,
    context: BackendErrorContext.APPLICATION_LINK,
    match: includes('Could not upload file', 'Could not get the content of the file', 'uploadToS3('),
  },
  {
    text: BackendErrorText.LINK_TEAM_NOT_IN_CHAMPIONSHIP,
    context: BackendErrorContext.APPLICATION_LINK,
    match: includes('not in championship'),
  },
  {
    text: BackendErrorText.LINK_CHAMPIONSHIP_NOT_FOUND,
    context: BackendErrorContext.APPLICATION_LINK,
    match: matches(/^Championship \d+ not found/),
  },
  { text: BackendErrorText.LINK_TEAM_NOT_FOUND, context: BackendErrorContext.APPLICATION_LINK, match: matches(/^Team \d+ not found/) },
];

/** Причина по случаю раздела «по тексту». */
export const BACKEND_ERROR_TEXT_MESSAGES: Record<BackendErrorText, MessageDescriptor> = {
  [BackendErrorText.STAGE_TYPE_NOT_SUPPORTED]: msg`Автогенерация недоступна для данного типа этапа. Поддерживаются: круговой и групповой форматы`,
  [BackendErrorText.STAGE_HAS_NO_TEAM_GROUPS]: msg`На этапе нет групп команд. Добавьте группы перед генерацией матчей`,
  [BackendErrorText.GROUP_NOT_ENOUGH_TEAMS]: msg`В одной из групп меньше 2 команд. Добавьте команды во все группы`,
  [BackendErrorText.STAGE_NOT_ENOUGH_TEAMS]: msg`Недостаточно команд на этапе. Добавьте минимум 2 команды`,
  [BackendErrorText.CARD_PAYMENTS_DISABLED]: msg`Оплата картой временно недоступна`,
  [BackendErrorText.APPLICATION_LIMIT_EXCEEDED]: msg`В команде уже максимум игроков по условиям турнира.`,
  [BackendErrorText.NOT_ENOUGH_MONEY]: msg`Недостаточно средств на балансе`,
  [BackendErrorText.RECEIPT_EMAIL_INVALID]: msg`Укажите email для чека`,
  [BackendErrorText.VALUE_ALREADY_USED]: msg`Указанный номер уже используется`,
  [BackendErrorText.LINK_TOKEN_MISSING]: msg`Ссылка неполная. Запросите новую у организатора`,
  // Срок жизни токена = дате окончания приёма заявок (`APPLICATION_END_DATE`).
  [BackendErrorText.LINK_TOKEN_EXPIRED]: msg`Срок подачи заявок истёк. Обратитесь к организатору за новой ссылкой`,
  [BackendErrorText.LINK_TOKEN_INVALID]: msg`Ссылка повреждена. Запросите новую у организатора`,
  [BackendErrorText.LINK_FIELD_NUMBER]: msg`Игровой номер должен быть числом от 1 до 99`,
  [BackendErrorText.LINK_FIELD_POSITION]: msg`Выберите амплуа из списка`,
  [BackendErrorText.LINK_FIELD_NAME]: msg`Укажите имя`,
  [BackendErrorText.LINK_FIELD_SURNAME]: msg`Укажите фамилию`,
  [BackendErrorText.LINK_FIELD_BIRTH_DATE]: msg`Укажите дату рождения`,
  [BackendErrorText.LINK_BIRTH_DATE_FORMAT]: msg`Проверьте дату рождения — она должна быть в формате 25.05.1990`,
  [BackendErrorText.LINK_PHOTO_TYPE_NOT_ALLOWED]: msg`Формат фото не поддерживается. Подойдут JPG, PNG или WebP`,
  [BackendErrorText.LINK_PHOTO_UPLOAD_FAILED]: msg`Заявка отправлена, но фото загрузить не удалось. Организатор добавит его вручную`,
  [BackendErrorText.LINK_TEAM_NOT_IN_CHAMPIONSHIP]: msg`Команда больше не участвует в этом турнире. Обратитесь к организатору`,
  [BackendErrorText.LINK_CHAMPIONSHIP_NOT_FOUND]: msg`Турнир не найден. Возможно, он удалён — обратитесь к организатору`,
  [BackendErrorText.LINK_TEAM_NOT_FOUND]: msg`Команда не найдена. Возможно, она удалена — обратитесь к организатору`,
};
